import PropTypes from 'prop-types';
import {
  StylesProvider as MUIStyleProvider,
  ThemeProvider as MuiThemeProvider,
} from '@material-ui/styles';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';

export const palette = {
  noir: {
    dark: '#1C2121',
    medium: '#283333',
  },
  grey: {
    dark: '#6C6C6C',
    medium: '#A6A6A6',
    mushroom: '#DED9D8',
    light: '#EAEAEA',
  },
  sorbet: {
    dark: '#CF5C30',
    medium: '#E77448',
    light: '#FFA380',
  },
  lilas: {
    dark: '#665E68',
    medium: '#A18E8E',
    light: '#EBE4E4',
  },
  beige: {
    darker: '#f6f0EB',
    dark: '#E6DBD2',
    medium: '#E6DBD2',
    light: '#FBF6F2',
  },
  vert: {
    dark: '#313520',
    medium: '#535744',
    light: '#9AA184',
  },
  rouge: {
    dark: '#833C33',
    medium: '#B45446',
  },
  blue: {
    medium: '#3C586C',
    light: '#9DB4C4',
  },
  white: {
    light: '#FFFFFF',
  },
  // These are Membership / The-salon new colors
  sage: {
    light: '#D4D7D6',
  },
  lime: {
    medium: '#e2ff5d',
    dark: '#B5D71B',
  },
  lavender: {
    light: '#f5f4fe',
    medium: '#d8d8f8',
  },
  earth: {
    light: '#f4ede5',
    medium: '#c7a37e',
  },
  forest: {
    medium: '#5a685d',
    dark: '#283833',
  },
  // Feedback
  green: {
    medium: '#686e63',
  },
  // Landing pages
  cream: {
    medium: '#fff6ea',
  },
  orange: {
    medium: '#e56849',
  },
  royal: {
    medium: '#ffd378',
  },
  silver: {
    medium: '#dbedff',
  },
  yellow: {
    light: '#ffe6b1',
    medium: '#ffcb53',
    dark: '#f4bb37',
  },
};

export const backgrounds = {
  beige: palette.beige.light,
  blue: palette.blue.medium,
  blueLight: palette.blue.light,
  darkerBeige: palette.beige.darker, // beige.dark already exist and is different from darkerBeige
  lilas: palette.lilas.light,
  lilasDark: palette.lilas.dark,
  noir: palette.noir.medium,
  noirDark: palette.noir.dark,
  rouge: palette.rouge.dark,
  vert: palette.vert.medium,
  veryLightVert: '#686e63',
  veryLightPink: '#EFE7E5',
  white: palette.white.light,
  sage: palette.sage.light,
  forest: palette.forest.dark,
  lavender: palette.lavender.medium,
  lavenderLight: palette.lavender.light,
  earthLight: palette.earth.light,
  earth: palette.earth.medium,
  lime: palette.lime.medium,
  cream: palette.cream.medium,
  royal: palette.royal.medium,
  silver: palette.silver.medium,
  lightGrey: palette.grey.light,
};

const shadows = [
  'none',
  '0 5px 5px -2px rgba(150, 150, 150, 0.2)',
  '0 0 2px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.24)',
];
Object.assign(shadows, { socialImpact: shadows[1], pdp: shadows[2] });

export const breakpoints = {
  xs: 360,
  sm: 768, // tablet width (portrait)
  md: 1024, // tablet width (landscape)
  lg: 1280, // desktop width
  xl: 1920,
};

const breakpointMedia = value => `@media (max-width: ${value - 0.05}px)`;

const breakpointMediaUp = value => `@media (min-width: ${value - 0.05}px)`;

export const breakpointMatches = value => {
  if (typeof value === 'string') {
    if (breakpoints[value]) {
      return breakpointMedia(breakpoints[value]);
    }
    throw new Error(
      `Expected value to be one of the values [${Object.keys(breakpoints).join(', ')}]`
    );
  } else if (typeof value === 'number') {
    return breakpointMedia(value);
  } else {
    throw new Error('Expected value to be a string or a number');
  }
};

const ipadProPortraitBreakpoints = `@media only screen and (min-device-width: 1024px) and (max-device-height: 1366px) and (-webkit-min-device-pixel-ratio: 2)  and (orientation: portrait)`;

const breakpointUp = value => {
  if (typeof value === 'string') {
    if (breakpoints[value]) {
      return breakpointMediaUp(breakpoints[value]);
    }
    throw new Error(
      `Expected value to be one of the values [${Object.keys(breakpoints).join(', ')}]`
    );
  } else if (typeof value === 'number') {
    return breakpointMediaUp(value);
  } else {
    throw new Error('Expected value to be a string or a number');
  }
};

const widths = {
  narrow: 327,
  half: 500,
  normal: 624,
  wide: 950,
  veryWide: 1200,
};

export const spacings = {
  s8: 8,
  s12: 12,
  s16: 16,
  s24: 24,
  s32: 32,
  s36: 36,
  s40: 40,
  s48: 48,
  s56: 56,
  s64: 64,
  s72: 72,
  s80: 80,
  s96: 96,
  s144: 144,
  s272: 272,
};

const utilsClasses = {
  resetButton: {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0,
    padding: 0,
  },
};

// typography constants
export const simplonMono = '"Simplon Mono", "Courier New", Courier, monospace';
export const simplonNorm = '"Simplon Norm", "Helvetica Neue", Helvetica, sans-serif';
export const saol = 'Saol, Times, "Times New Roman", serif';
const letterSpacingExtra = '0.09em';
const letterSpacingNormal = '0.06em';
const lineHeightNormal = '1.43em';

export const saolDefaults = {
  fontFamily: saol,
  lineHeight: '1.3em',
  letterSpacing: 0.5,
  fontWeight: 400,
};

export const simplonMonoDefaults = {
  fontFamily: simplonMono,
  lineHeight: '1.5em',
  letterSpacing: 1,
  textTransform: 'uppercase',
  fontWeight: 400,
};

export const simplonNormDefaults = {
  fontFamily: simplonNorm,
  lineHeight: '1.5em',
  letterSpacing: 0.5,
  fontWeight: 400,
};

export const typographyVariants = {
  hero1: {
    ...saolDefaults,
    lineHeight: '1.15em',
    fontSize: 70,
    letterSpacing: 1,
    [breakpointMatches('sm')]: {
      fontSize: 42,
    },
  },
  hero2: {
    ...saolDefaults,
    lineHeight: '1.15em',
    fontSize: 48,
    letterSpacing: 1,
  },
  'hero-mono': {
    ...simplonMonoDefaults,
    lineHeight: '1.15em',
    fontSize: 70,
    [breakpointMatches('sm')]: {
      fontSize: 48,
    },
  },
  'h1r-mono': {
    ...simplonMonoDefaults,
    lineHeight: '1.15em',
    fontSize: 36,
    [breakpointMatches('sm')]: {
      fontSize: 28,
    },
  },
  'h2r-mono': {
    ...simplonMonoDefaults,
    lineHeight: '1.15em',
    fontSize: 24,
    // [breakpointMatches('sm')]: {
    //   fontSize: 24,
    // },
  },
  h1r: {
    ...saolDefaults,
    fontSize: 36,
    [breakpointMatches('sm')]: {
      fontSize: 28,
    },
  },
  h2r: {
    ...saolDefaults,
    fontSize: 28,
    [breakpointMatches('sm')]: {
      fontSize: 20,
    },
  },
  h1: {
    ...saolDefaults,
    fontSize: 36,
    letterSpacing: 1,
  },
  h2: {
    ...saolDefaults,
    fontSize: 28,
  },
  h3: {
    ...saolDefaults,
    fontSize: 20,
  },
  h4: {
    ...saolDefaults,
    fontSize: 16,
  },
  mono1: {
    ...simplonMonoDefaults,
    fontSize: 15,
  },
  mono2: {
    ...simplonMonoDefaults,
    fontSize: 13,
  },
  mono3: {
    ...simplonMonoDefaults,
    fontSize: 11,
  },
  mono4: {
    ...simplonMonoDefaults,
    fontSize: 8,
  },
  p1: {
    ...simplonNormDefaults,
    fontSize: 16,
  },
  p2: {
    ...simplonNormDefaults,
    fontSize: 14,
  },
  p3: {
    ...simplonNormDefaults,
    fontSize: 12,
  },
  p4: {
    ...simplonNormDefaults,
    fontSize: 11,
  },
};

// other constants
const bannerHeight = 40;
const navbarHeight = 55;
const buttonHeight = 53;
export const headerHeight = bannerHeight + navbarHeight;

export const fullHeight = () => typeof window !== 'undefined' && window.innerHeight - headerHeight;
const fullHeightWithFixedCTA = () =>
  typeof window !== 'undefined' && window.innerHeight - headerHeight - buttonHeight;

const theme = {
  breakpoints: {
    values: breakpoints,
    match: breakpointMatches,
    ipadProPortrait: ipadProPortraitBreakpoints,
    up: breakpointUp,
  },
  utils: {
    ...utilsClasses,
  },
  palette: {
    common: palette,
    primary: {
      main: palette.beige.light, // mandatory for MUI theme
      dark: palette.sorbet.light, // will be used by the text input underline
    },
    backgrounds,
  },
  props: {
    minimumBlockPadding: spacings.s24,
    verticalSpacingMob: spacings.s72,
    verticalSpacingDes: spacings.s144,
    buttonHeight,
    bannerHeight,
    navbarHeight,
    headerHeight,
    fullHeight: fullHeight(),
    fullHeightWithFixedCTA: fullHeightWithFixedCTA(),
    checkboxSize: { small: 16, medium: 28, large: 40 },
  },
  width: widths,
  spacing: spacings,
  typography: {
    useNextVariants: true,
    fontFamily: simplonMono,
    letterSpacingExtra,
    letterSpacingNormal,
    lineHeightNormal,
    simplonMono,
    simplonNorm,
    saol,
    ...typographyVariants,
  },
  zIndex: {
    mobileStepper: 1000,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  shadows,
};

const ThemeProvider = ({ children }) => (
  <MUIStyleProvider injectFirst>
    <MuiThemeProvider theme={theme}>
      <EmotionThemeProvider theme={theme}>{children}</EmotionThemeProvider>
    </MuiThemeProvider>
  </MUIStyleProvider>
);

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeProvider;
